import { EBroadcastChannel } from 'constants/enums';

import type { IStoreRefreshEligibilityOffer } from './useStoreRefresh.types';
import {
  DISMISSED_AT_PREFIX,
  SHOWING_STALE_MILLISECONDS,
} from './useStoreRefresh.consts';

export type { IStoreRefreshEligibilityOffer };

const TAB_ID = crypto.randomUUID();
const closedOfferIdsThisSession = new Set<string>();

interface IShowingLockPayload {
  tabId: string;
  timestampMilliseconds: number;
}

export const getClosedOfferIdsThisSession = (): ReadonlySet<string> =>
  closedOfferIdsThisSession;

export const markOfferClosedThisSession = (
  offerId: string,
): ReadonlySet<string> => {
  closedOfferIdsThisSession.add(offerId);
  return new Set(closedOfferIdsThisSession);
};

export const clearStoreRefreshSession = (): void => {
  closedOfferIdsThisSession.clear();
};

export const isAnotherTabShowing = (): boolean => {
  const rawValue = localStorage.getItem(
    EBroadcastChannel.STORE_REFRESH_SHOWING_KEY,
  );
  if (rawValue === null) return false;
  try {
    const { tabId, timestampMilliseconds } = JSON.parse(
      rawValue,
    ) as IShowingLockPayload;
    if (tabId === TAB_ID) return false;
    if (!Number.isFinite(timestampMilliseconds)) return false;
    return Date.now() - timestampMilliseconds < SHOWING_STALE_MILLISECONDS;
  } catch {
    return false;
  }
};

export const writeShowingLock = (): void => {
  const payload: IShowingLockPayload = {
    tabId: TAB_ID,
    timestampMilliseconds: Date.now(),
  };
  localStorage.setItem(
    EBroadcastChannel.STORE_REFRESH_SHOWING_KEY,
    JSON.stringify(payload),
  );
};

export const clearShowing = (): void => {
  const rawValue = localStorage.getItem(
    EBroadcastChannel.STORE_REFRESH_SHOWING_KEY,
  );
  if (rawValue === null) return;
  try {
    const { tabId } = JSON.parse(rawValue) as Pick<IShowingLockPayload, 'tabId'>;
    if (tabId !== TAB_ID) return;
  } catch {
    // Corrupted entry — safe to remove
  }
  localStorage.removeItem(EBroadcastChannel.STORE_REFRESH_SHOWING_KEY);
};

export const getDismissedAtMilliseconds = (offerId: string): number | null => {
  const rawValue = localStorage.getItem(DISMISSED_AT_PREFIX + offerId);
  if (rawValue === null) return null;
  const parsedMilliseconds = Number(rawValue);
  return Number.isFinite(parsedMilliseconds) ? parsedMilliseconds : null;
};

export const markDismissed = (offerId: string): void => {
  localStorage.setItem(DISMISSED_AT_PREFIX + offerId, String(Date.now()));
};

const resolveStoreRefreshLatestInteractionMilliseconds = (
  offer: IStoreRefreshEligibilityOffer,
): number | null => {
  let latestMilliseconds: number | null = null;

  const lastInteractionTime = offer.displayRule?.lastInteractionTime;
  if (lastInteractionTime) {
    const lastInteractionMilliseconds = new Date(lastInteractionTime).getTime();
    if (Number.isFinite(lastInteractionMilliseconds)) {
      latestMilliseconds = lastInteractionMilliseconds;
    }
  }

  const dismissedAtMilliseconds = getDismissedAtMilliseconds(offer.offerId);
  if (
    dismissedAtMilliseconds !== null &&
    (latestMilliseconds === null || dismissedAtMilliseconds > latestMilliseconds)
  ) {
    latestMilliseconds = dismissedAtMilliseconds;
  }

  return latestMilliseconds;
};

export const getCooldownEndsAtMilliseconds = (
  offer: IStoreRefreshEligibilityOffer,
): number | null => {
  const cooldownInMinutes = offer.displayRule?.cooldownInMinutes ?? 0;
  if (!Number.isFinite(cooldownInMinutes) || cooldownInMinutes <= 0) return null;

  const latestMilliseconds = resolveStoreRefreshLatestInteractionMilliseconds(offer);
  if (latestMilliseconds === null) return null;

  const cooldownInMilliseconds = cooldownInMinutes * 60_000;
  const endsAt = latestMilliseconds + cooldownInMilliseconds;
  if (Date.now() >= endsAt) return null;
  return endsAt;
};

export const isOfferOnCooldown = (
  offer: IStoreRefreshEligibilityOffer,
): boolean => getCooldownEndsAtMilliseconds(offer) !== null;

export const isOfferEligible = (
  offer: IStoreRefreshEligibilityOffer,
  count: number,
): boolean => {
  const frequency = offer.displayRule?.frequency ?? 1;
  if (count < frequency) return false;
  return !isOfferOnCooldown(offer);
};

export const pickEligibleStoreRefreshOffer = <
  T extends IStoreRefreshEligibilityOffer,
>(
  offers: T[],
  count: number,
): T | undefined => {
  const eligible = offers.filter((offer) => isOfferEligible(offer, count));
  if (eligible.length === 0) return undefined;
  return eligible.sort(
    (offerA, offerB) => (offerA.priority ?? 0) - (offerB.priority ?? 0),
  )[0];
};
